import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useFeatureFlags } from '../hooks/useFeatureFlags';
import { getAuthToken } from '../lib/authStorage';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  LayoutDashboard,
  Package,
  ShoppingCart,
  FolderTree,
  Users,
  Settings,
  Store,
  ChevronDown,
  LogOut,
  Plus,
  Menu,
  X,
  Ticket,
  BarChart3,
  MapPin,
  Building2,
  Monitor,
  ChefHat,
  Hotel,
  UserCog,
  QrCode,
  CreditCard,
  FileText,
  Palette,
  Sparkles,
  Heart,
  Gift,
  Globe,
  Crown,
  Search,
  Bell,
  PanelLeftClose,
  PanelLeftOpen,
  Warehouse,
  Eye,
  Truck,
  Coins,
  Inbox
} from 'lucide-react';
import LanguageSwitcher from './LanguageSwitcher';
import ThemeSwitcher from './ThemeSwitcher';
import AydaCredit from './AydaCredit';
import { toast } from 'sonner';

export const DashboardLayout = () => {
  const { user, stores, currentStore, switchStore, logout } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    return localStorage.getItem('woyof_sidebar_collapsed') === 'true';
  });
  const { isEnabled, getVisibility } = useFeatureFlags();
  const [importNotifications, setImportNotifications] = useState([]);
  const notifPollRef = useRef(null);

  // Poll for import notifications every 15s
  const checkImportNotifications = useCallback(async () => {
    if (!currentStore?.id) return;
    try {
      const token = getAuthToken();
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/stores/${currentStore.id}/import-notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        if (data.length > 0 && importNotifications.length === 0) {
          const done = data.filter(n => n.status === 'review_ready');
          const failed = data.filter(n => n.status === 'failed');
          if (done.length > 0) toast.success(`Import AI terminé ! ${done[0].item_count || 0} article(s) extraits.`);
          if (failed.length > 0) toast.error("Un import AI a échoué.");
        }
        setImportNotifications(data);
      }
    } catch {}
  }, [currentStore?.id, importNotifications.length]);

  useEffect(() => {
    checkImportNotifications();
    notifPollRef.current = setInterval(checkImportNotifications, 15000);
    return () => clearInterval(notifPollRef.current);
  }, [checkImportNotifications]);

  const dismissNotifications = async () => {
    if (!currentStore?.id) return;
    try {
      const token = getAuthToken();
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/stores/${currentStore.id}/import-notifications/dismiss`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      setImportNotifications([]);
    } catch {}
  };

  const toggleSidebarCollapse = () => {
    const next = !sidebarCollapsed;
    setSidebarCollapsed(next);
    localStorage.setItem('woyof_sidebar_collapsed', String(next));
  };

  // Modules that are feature-gated
  const FEATURE_GATED = {
    '/dashboard/pos': { key: 'pos', label: 'Point de Vente' },
    '/dashboard/kds': { key: 'kds', label: 'Écran Cuisine' },
    '/dashboard/hotel': { key: 'hotel', label: 'Réservations Hôtel' },
    '/dashboard/ai-import': { key: 'ai_import', label: 'AI Import' },
  };

  // Plan-based gating: paths only visible for certain plans (unless individually unlocked)
  const PLAN_GATED = {
    '/dashboard/domains': {
      allowedPlans: ['pro', 'enterprise'],
      unlockField: 'domain_tab_unlocked',
      label: 'Domains',
    },
  };
  const isPlanAllowed = (path) => {
    const gate = PLAN_GATED[path];
    if (!gate) return true;
    if (user?.[gate.unlockField]) return true;
    const plan = (user?.selected_plan || '').toLowerCase();
    return gate.allowedPlans.includes(plan);
  };

  const menuItems = [
    { icon: LayoutDashboard, label: t('common.dashboard'), path: '/dashboard', testId: 'nav-dashboard' },
    { icon: Store, label: 'Vitrine', path: '/dashboard/storefront', testId: 'nav-storefront' },
    { icon: Search, label: 'SEO', path: '/dashboard/seo', testId: 'nav-seo' },
    { icon: Sparkles, label: 'AI Import', path: '/dashboard/ai-import', testId: 'nav-ai-import' },
    { icon: Coins, label: 'AI Credits', path: '/dashboard/ai-credits', testId: 'nav-ai-credits' },
    { icon: Package, label: t('common.products'), path: '/dashboard/products', testId: 'nav-products' },
    { icon: Warehouse, label: 'Inventaire', path: '/dashboard/inventory', testId: 'nav-inventory' },
    { icon: FolderTree, label: t('common.categories'), path: '/dashboard/categories', testId: 'nav-categories' },
    { icon: ShoppingCart, label: t('common.orders'), path: '/dashboard/orders', testId: 'nav-orders' },
    { icon: ShoppingCart, label: t('abandonedCart.abandonedCarts'), path: '/dashboard/abandoned-carts', testId: 'nav-abandoned-carts' },
    { icon: Users, label: t('common.customers'), path: '/dashboard/customers', testId: 'nav-customers' },
    { icon: Inbox, label: 'Form submissions', path: '/dashboard/form-submissions', testId: 'nav-form-submissions' },
    { icon: Ticket, label: 'Discounts', path: '/dashboard/coupons', testId: 'nav-coupons' },
    { icon: Users, label: 'Creators', path: '/dashboard/creators', testId: 'nav-creators' },
    { icon: MapPin, label: 'Delivery Zones', path: '/dashboard/delivery-zones', testId: 'nav-delivery-zones' },
    { icon: Building2, label: 'Succursales', path: '/dashboard/branches', testId: 'nav-branches' },
    { icon: CreditCard, label: 'Payments', path: '/dashboard/payments', testId: 'nav-payments' },
    { icon: Monitor, label: 'Point de Vente', path: '/dashboard/pos', testId: 'nav-pos' },
    { icon: ChefHat, label: 'Écran Cuisine', path: '/dashboard/kds', testId: 'nav-kds' },
    { icon: Hotel, label: 'Réservations Hôtel', path: '/dashboard/hotel', testId: 'nav-hotel' },
    { icon: UserCog, label: 'Personnel', path: '/dashboard/staff', testId: 'nav-staff' },
    { icon: QrCode, label: 'QR Codes', path: '/dashboard/qr-codes', testId: 'nav-qr-codes' },
    { icon: FileText, label: 'Construire', path: '/dashboard/content', testId: 'nav-content' },
    { icon: Palette, label: 'Themes', path: '/dashboard/themes', testId: 'nav-themes' },
    { icon: Heart, label: 'Loyalty', path: '/dashboard/loyalty', testId: 'nav-loyalty' },
    { icon: Gift, label: 'Gift Cards', path: '/dashboard/gift-cards', testId: 'nav-gift-cards' },
    { icon: Globe, label: 'Domains', path: '/dashboard/domains', testId: 'nav-domains' },
    { icon: BarChart3, label: t('common.analytics'), path: '/dashboard/analytics', testId: 'nav-analytics' },
    { icon: Eye, label: 'Live Customers', path: '/dashboard/live-customers', testId: 'nav-live-customers' },
    { icon: Truck, label: 'Live Deliveries', path: '/dashboard/live-deliveries', testId: 'nav-live-deliveries' },
    { icon: Bell, label: 'Notifications', path: '/dashboard/notification-preferences', testId: 'nav-notifications' },
    { icon: Crown, label: 'Abonnement', path: '/dashboard/subscription', testId: 'nav-subscription' },
    { icon: Settings, label: t('common.settings'), path: '/dashboard/settings', testId: 'nav-settings' },
  ];

  const isActive = (path) => location.pathname === path;

  if (!currentStore) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background" data-testid="no-store-message">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">{t('store.myStores')}</h2>
          <p className="text-muted-foreground mb-6">{t('store.createStore')}</p>
          <Button onClick={() => navigate('/store-setup')} data-testid="create-first-store-button">
            <Plus className="mr-2 h-4 w-4" />
            {t('store.createStore')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="dashboard-layout">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 ${sidebarCollapsed ? 'w-[68px]' : 'w-64'} bg-card border-r border-border transform transition-all duration-200 ease-in-out ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`} data-testid="dashboard-sidebar">
        <div className="flex flex-col h-full">
          {/* Logo & Store Switcher */}
          <div className={`${sidebarCollapsed ? 'p-3' : 'p-6'} border-b border-border`}>
            <Link to="/" className="flex items-center justify-center mb-4">
              <img src="/woyof-logo.png" alt="Woyof" className={`${sidebarCollapsed ? 'h-8' : 'h-12'} object-contain`} data-testid="dashboard-logo" />
            </Link>
            
            {!sidebarCollapsed && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="w-full justify-between" data-testid="store-switcher">
                    <span className="truncate">{currentStore.name}</span>
                    <ChevronDown className="h-4 w-4 ml-2" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56">
                  <DropdownMenuLabel>{t('store.myStores')}</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {stores.map((store) => (
                    <DropdownMenuItem
                      key={store.id}
                      onClick={() => switchStore(store)}
                      className={currentStore.id === store.id ? 'bg-muted' : ''}
                      data-testid={`store-option-${store.id}`}
                    >
                      {store.name}
                    </DropdownMenuItem>
                  ))}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/store-setup')} data-testid="create-new-store">
                    <Plus className="mr-2 h-4 w-4" />
                    {t('store.createStore')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>

          {/* Navigation */}
          <nav className={`flex-1 ${sidebarCollapsed ? 'p-2' : 'p-4'} space-y-1 overflow-y-auto`}>
            {menuItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              const gate = FEATURE_GATED[item.path];
              const featureActive = gate ? isEnabled(gate.key) : true;
              const visibility = gate ? getVisibility(gate.key) : 'enabled';
              const isHidden = gate && !featureActive && visibility === 'hidden';

              if (isHidden) return null;

              // Plan-based gating: hide entries that the user's plan doesn't grant access to
              if (PLAN_GATED[item.path] && !isPlanAllowed(item.path)) return null;

              const isDisabled = gate && !featureActive;

              return (
                <Link
                  key={item.path}
                  to={isDisabled ? item.path : item.path}
                  className={`flex items-center ${sidebarCollapsed ? 'justify-center' : 'gap-3'} rounded-lg ${sidebarCollapsed ? 'px-2 py-2.5' : 'px-3 py-2'} transition-all ${
                    isDisabled
                      ? 'text-muted-foreground/40 cursor-default'
                      : active
                      ? 'text-primary bg-primary/10 font-medium'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                  }`}
                  onClick={() => setSidebarOpen(false)}
                  data-testid={item.testId}
                  title={sidebarCollapsed ? item.label : undefined}
                >
                  <Icon className={`h-5 w-5 shrink-0 ${isDisabled ? 'opacity-40' : ''}`} />
                  {!sidebarCollapsed && <span className={isDisabled ? 'opacity-40' : ''}>{item.label}</span>}
                  {!sidebarCollapsed && isDisabled && (
                    <span className="ml-auto text-[10px] bg-muted text-muted-foreground px-1.5 py-0.5 rounded font-medium">
                      Soon
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>

          {/* Collapse Toggle */}
          <div className="px-2 py-2 border-t border-border hidden lg:flex justify-center">
            <button
              onClick={toggleSidebarCollapse}
              className="h-8 w-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              data-testid="sidebar-collapse-btn"
              title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {sidebarCollapsed ? <PanelLeftOpen className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
            </button>
          </div>

          {/* Language & Theme */}
          <div className={`${sidebarCollapsed ? 'p-2 flex-col' : 'p-4'} border-t border-border flex items-center gap-2`}>
            {/* Import notification bell */}
            {importNotifications.length > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="relative" data-testid="import-notification-bell">
                    <Bell className="h-4 w-4" />
                    <span className="absolute -top-0.5 -right-0.5 h-4 w-4 rounded-full bg-primary text-[10px] text-primary-foreground flex items-center justify-center">{importNotifications.length}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64">
                  <DropdownMenuLabel>Notifications d'import</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {importNotifications.map((n, i) => (
                    <DropdownMenuItem key={i} onClick={() => { dismissNotifications(); navigate('/dashboard/ai-import'); }}>
                      <div className="flex flex-col gap-0.5">
                        <span className="text-xs font-medium">{n.status === 'review_ready' ? `Import terminé — ${n.item_count || 0} articles` : 'Import échoué'}</span>
                        <span className="text-[10px] text-muted-foreground">{n.file_count || 0} fichier(s)</span>
                      </div>
                    </DropdownMenuItem>
                  ))}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={dismissNotifications} className="text-xs text-muted-foreground justify-center">Tout marquer comme lu</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            {!sidebarCollapsed && <LanguageSwitcher variant="compact" />}
            <ThemeSwitcher variant="toggle" />
          </div>

          {/* User Menu */}
          <div className={`${sidebarCollapsed ? 'p-2' : 'p-4'} border-t border-border`}>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className={`w-full ${sidebarCollapsed ? 'justify-center px-0' : 'justify-start'}`} data-testid="user-menu">
                  <div className={`flex items-center ${sidebarCollapsed ? '' : 'gap-3'}`}>
                    <div className="h-8 w-8 rounded-full bg-primary flex items-center justify-center text-primary-foreground text-sm font-medium shrink-0">
                      {user?.name?.charAt(0).toUpperCase()}
                    </div>
                    {!sidebarCollapsed && (
                      <div className="flex-1 text-left min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">{user?.name}</p>
                        <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                      </div>
                    )}
                  </div>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem onClick={logout} data-testid="logout-button">
                  <LogOut className="mr-2 h-4 w-4" />
                  {t('common.logout')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </aside>

      {/* Mobile menu button */}
      <div className="lg:hidden fixed top-4 left-4 z-50">
        <Button
          variant="outline"
          size="icon"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="bg-card"
          data-testid="mobile-menu-button"
        >
          {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </Button>
      </div>

      {/* Top bar for mobile - Language & Theme */}
      <div className="lg:hidden fixed top-4 right-4 z-50 flex items-center gap-2">
        {importNotifications.length > 0 && (
          <Button variant="outline" size="icon" className="relative bg-card" onClick={() => { dismissNotifications(); navigate('/dashboard/ai-import'); }} data-testid="mobile-notification-bell">
            <Bell className="h-4 w-4" />
            <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-primary text-[10px] text-primary-foreground flex items-center justify-center">{importNotifications.length}</span>
          </Button>
        )}
        <LanguageSwitcher variant="compact" />
        <ThemeSwitcher variant="toggle" />
      </div>

      {/* Main content */}
      <main className={`${sidebarCollapsed ? 'lg:pl-[68px]' : 'lg:pl-64'} transition-all duration-200`}>
        <div className="p-6 lg:p-8 pt-16 lg:pt-8 min-h-screen flex flex-col">
          <div className="flex-1">
            <Outlet />
          </div>
          <div className="pt-8 pb-4">
            <AydaCredit />
          </div>
        </div>
      </main>

      {/* Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};